/**
 * Geodesy and angle helpers.
 *
 * Runtime-agnostic on purpose: no Node built-ins, no DOM globals. Both the server and the
 * browser import this module, so client and server can never disagree about the maths.
 *
 * Angle convention across the whole product: **degrees clockwise from true north, in [0, 360)**.
 */

/** Mean Earth radius in metres (IUGG). */
export const EARTH_RADIUS_M = 6_371_008.8;

const DEG = Math.PI / 180;
const RAD = 180 / Math.PI;

/** A position on the Earth's surface. */
export interface LatLng {
  lat: number;
  lng: number;
}

export const toRadians = (deg: number): number => deg * DEG;
export const toDegrees = (rad: number): number => rad * RAD;

/**
 * Normalise any angle to [0, 360).
 * `normalizeDegrees(-90) === 270`, `normalizeDegrees(360) === 0`.
 */
export function normalizeDegrees(deg: number): number {
  if (!Number.isFinite(deg)) return 0;
  const wrapped = deg % 360;
  const positive = wrapped < 0 ? wrapped + 360 : wrapped;
  // Two floating-point traps to close, both of which would otherwise leak into the scene:
  // `-360 % 360` is -0, and adding 360 to a tiny negative rounds up to exactly 360.
  return positive >= 360 || positive === 0 ? 0 : positive;
}

/**
 * Signed shortest angular difference `to - from`, in (-180, 180].
 *
 * This is the primitive that stops the arrow spinning the long way around:
 * going from 350° to 10° yields +20, not -340.
 */
export function shortestAngleDelta(from: number, to: number): number {
  const diff = normalizeDegrees(to - from);
  return diff > 180 ? diff - 360 : diff;
}

/**
 * Interpolate between two angles along the shortest path.
 *
 * @param t 0 returns `from`, 1 returns `to`. Values outside [0, 1] are clamped.
 */
export function lerpAngle(from: number, to: number, t: number): number {
  const clamped = t < 0 ? 0 : t > 1 ? 1 : t;
  return normalizeDegrees(from + shortestAngleDelta(from, to) * clamped);
}

/**
 * Frame-rate independent angular smoothing.
 *
 * `smoothing` is the fraction of the remaining error left after one second — so 0.02 means
 * "close 98% of the gap per second". Independent of how often this is called, which matters
 * because the render loop is not a fixed timestep.
 */
export function smoothAngle(
  current: number,
  target: number,
  deltaSeconds: number,
  smoothing: number,
): number {
  if (deltaSeconds <= 0) return normalizeDegrees(current);
  const factor = 1 - Math.pow(smoothing, deltaSeconds);
  return lerpAngle(current, target, factor);
}

/**
 * Great-circle distance in metres (haversine).
 *
 * Haversine is well-conditioned at the short ranges this product cares about and is what
 * `ProductOverview.md` §4.3 specifies.
 */
export function haversineDistance(a: LatLng, b: LatLng): number {
  const lat1 = toRadians(a.lat);
  const lat2 = toRadians(b.lat);
  const dLat = toRadians(b.lat - a.lat);
  const dLng = toRadians(b.lng - a.lng);

  const sinLat = Math.sin(dLat / 2);
  const sinLng = Math.sin(dLng / 2);
  const h = sinLat * sinLat + Math.cos(lat1) * Math.cos(lat2) * sinLng * sinLng;

  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Initial great-circle bearing from `a` to `b`, in degrees clockwise from true north.
 *
 * "Initial" matters: over long distances a great circle's bearing changes as you travel it.
 * At Beeline's ranges the difference is irrelevant, but the arrow always shows the bearing
 * from where you are *now*, which is exactly the initial bearing recomputed every fix.
 */
export function initialBearing(a: LatLng, b: LatLng): number {
  const lat1 = toRadians(a.lat);
  const lat2 = toRadians(b.lat);
  const dLng = toRadians(b.lng - a.lng);

  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);

  return normalizeDegrees(toDegrees(Math.atan2(y, x)));
}

/**
 * Move `metres` from `origin` along `bearing`. Used by the simulator to fly a fake peer
 * through the real WebSocket path.
 */
export function destinationPoint(origin: LatLng, bearing: number, metres: number): LatLng {
  const angular = metres / EARTH_RADIUS_M;
  const brg = toRadians(bearing);
  const lat1 = toRadians(origin.lat);
  const lng1 = toRadians(origin.lng);

  const sinLat2 =
    Math.sin(lat1) * Math.cos(angular) + Math.cos(lat1) * Math.sin(angular) * Math.cos(brg);
  const lat2 = Math.asin(Math.min(1, Math.max(-1, sinLat2)));
  const lng2 =
    lng1 +
    Math.atan2(
      Math.sin(brg) * Math.sin(angular) * Math.cos(lat1),
      Math.cos(angular) - Math.sin(lat1) * sinLat2,
    );

  return {
    lat: toDegrees(lat2),
    // Keep longitude in [-180, 180] so it round-trips through JSON and the UI cleanly.
    lng: normalizeDegrees(toDegrees(lng2) + 180) - 180,
  };
}

const COMPASS_POINTS = [
  'north',
  'northeast',
  'east',
  'southeast',
  'south',
  'southwest',
  'west',
  'northwest',
] as const;

export type CompassPoint = (typeof COMPASS_POINTS)[number];

/** Spoken direction for the north-up fallback view ("northeast, 240 m"). */
export function compassPoint(bearing: number): CompassPoint {
  const index = Math.round(normalizeDegrees(bearing) / 45) % 8;
  return COMPASS_POINTS[index] ?? 'north';
}

/**
 * Distance as shown to the user: metres below a kilometre, kilometres with two decimals above.
 * (`ProductOverview.md` §4.3.)
 */
export function formatDistance(metres: number): string {
  if (!Number.isFinite(metres) || metres < 0) return '—';
  if (metres < 1000) return `${Math.round(metres)} m`;
  return `${(metres / 1000).toFixed(2)} km`;
}
